/* eslint-disable no-param-reassign */
import { useState, useEffect, useRef } from 'react'
import { trackWindowScroll } from 'react-lazy-load-image-component'
import withStyles from '@material-ui/core/styles/withStyles'
import map from 'ramda/src/map'
import includes from 'ramda/src/includes'
import propEq from 'ramda/src/propEq'
import filter from 'ramda/src/filter'
import pipe from 'ramda/src/pipe'
import splitEvery from 'ramda/src/splitEvery'
import pluck from 'ramda/src/pluck'
import keys from 'ramda/src/keys'
import forEach from 'ramda/src/forEach'
import compose from 'ramda/src/compose'
import prop from 'ramda/src/prop'
import __ from 'ramda/src/__'
import MultiChoiceForm from 'root/src/client/web/base/MultiChoiceForm'
import Pager from 'root/src/client/web/base/Pager'
import {
	typeKey,
	levelKey,
	idKey,
	platformKey,
} from 'root/src/shared/constants/keys'
import Typography, { typographyVariants } from 'root/src/client/web/typography/Typography'
import DareCardDrop from 'root/src/client/web/list/listItemComponents/dare/DareCardDrop'
import {
	ltGtMediaQuery,
	gtLgMediaQuery,
	gtSmMediaQuery,
} from 'root/src/client/web/commonStyles'
import anchors from 'root/src/shared/constants/anchors'
import providers from 'root/src/shared/constants/providers'
import { RequirementDareMessages } from 'root/src/client/web/record/DropEvent/RequirementDareMessages'

const style = {
	daresSection: {
		margin: [[50, 'auto']],
		width: '100%',
		overflow: 'hidden',
		[ltGtMediaQuery]: {
			width: '100%',
		},
		[gtLgMediaQuery]: {
			width: '100%',
		},
		[gtSmMediaQuery]: {
		},
	},
	daresHeadingContainer: {
		margin: [[22, 'auto']],
		[ltGtMediaQuery]: {
			textAlign: 'center',
		},
	},
	widgetsContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		[ltGtMediaQuery]: {
			flexFlow: 'column wrap',
			justifyContent: 'center',
		},
	},
	daresContainer: {
		display: 'grid',
		marginTop: 30,
		transform: 'translateX(100%)',
		gridTemplateColumns: 'repeat(3, 1fr)',
		justifyItems: 'center',

		'@media (max-width: 1100px)': {
			gridTemplateColumns: 'repeat(2, 1fr)',
		},
		'@media (max-width: 650px)': {
			gridTemplateColumns: '1fr',
		},
	},
	card: {
		width: 'fit-content',
		margin: [[20, 0]],
		transitionDuration: '.3s',
	},
	maxDaresText: {
		textAlign: 'center',
		margin: [[30, 0]],
	},
}

const setStyles = (element, styles) => {
	forEach((property) => {
		element.style[property] = styles[property]
	},
	keys(styles))
}
const setNestedStyles = (ref, styles) => forEach(
	child => setStyles(child, styles),
	ref.current.children,
)

const animationActions = {
	page: 'page',
	filter: 'filter',
}

const DaresSection = ({
	classes, dares, gameName, content, canAccept, campaignId, submitting, setSubmitting,
	emailSubscription, toggleEmailSubscription, comingSoon, canAccessCreateBrandProject,
	showTermsModal, pointsToEarn, canEarnPoints, isAbleToParticipate,
}) => {
	const itemsAmount = 6
	const { primaryHeading, secondaryHeading, filters } = content
	const [typeFilters, setTypeFilters] = useState(filters[typeKey])
	const [levelFilters, setLevelFilters] = useState(filters[levelKey])
	const [platformFilters, setPlatformFilters] = useState(filters[platformKey])
	const [currentPage, setCurrentPage] = useState(1)
	const [allPage, setAllPage] = useState(Math.ceil(dares.length / itemsAmount))
	const [pageItems, setPageItems] = useState(splitEvery(itemsAmount, dares)[0] || [])
	const wrapperRef = useRef()
	const actionTypeRef = useRef(null)

	const filterDares = () => {
		const getActiveFilters = pipe(
			filter(propEq('active', true)),
			pluck('id'),
		)
		const filterList = [
			{
				type: typeKey,
				value: getActiveFilters(typeFilters),
			},
			{
				type: levelKey,
				value: getActiveFilters(levelFilters),
			},
			{
				type: platformKey,
				value: getActiveFilters(platformFilters),
			},
		]

		const newDares = filterList.reduce(
			(acc, { value, type }) => acc.filter(dare => value.includes(dare[type])
			|| (type === platformKey && dare[type] === providers.any)), dares,
		)

		const currentPageIndex = currentPage - 1

		setAllPage(Math.ceil(newDares.length / itemsAmount) || 1)
		setPageItems(splitEvery(itemsAmount, newDares)[currentPageIndex] || [])
	}

	const disappearingAnimation = () => {
		setNestedStyles(wrapperRef, {
			transitionDuration: '.3s',
			transitionTimingFunction: 'cubic-bezier(.27,-0.48,1,.81)',
			transform: 'scale(0)',
		})
		setTimeout(() => {
			setStyles(wrapperRef.current, {
				transitionDuration: '0s',
				transform: 'translateX(100%)',
			})
		}, 300)
	}

	const appearingAnimation = () => {
		setTimeout(() => {
			filterDares()
			setNestedStyles(wrapperRef, {
				transitionDuration: '0s',
				transform: 'scale(1)',
			})
			setStyles(wrapperRef.current, {
				transitionDuration: '.2s',
				transitionTimingFunction: 'ease-in',
				transform: 'translateX(0)',
			})
		}, 320)
	}

	const getAvailableFilters = (key, allFilters) => filter(
		compose(includes(__, pluck(key, dares)), prop(idKey)), allFilters,
	)

	useEffect(() => {
		filterDares()
	}, [dares])

	useEffect(() => {
		disappearingAnimation()
		if (actionTypeRef.current === animationActions.filter) {
			if (currentPage === 1) {
				appearingAnimation()
			} else {
				setCurrentPage(1)
			}
		} else {
			appearingAnimation()
		}
		actionTypeRef.current = null
	}, [currentPage, typeFilters, levelFilters, platformFilters])

	return (
		<div
			id={anchors.DROP_ALL_DARES}
			className={classes.daresSection}
		>
			<div className={classes.daresHeadingContainer}>
				<Typography variant={typographyVariants.heading3}>{primaryHeading}</Typography>
				<Typography variant={typographyVariants.heading2}>{secondaryHeading(gameName)}</Typography>
			</div>
			<RequirementDareMessages />
			<div className={classes.widgetsContainer}>
				<MultiChoiceForm
					label="Type:"
					options={getAvailableFilters(typeKey, typeFilters)}
					handler={(newOptions) => {
						setCurrentPage(1)
						actionTypeRef.current = animationActions.filter
						setTypeFilters(newOptions)
					}}
				/>
				<MultiChoiceForm
					label="Channel:"
					options={getAvailableFilters(platformKey, platformFilters)}
					handler={(newOptions) => {
						setCurrentPage(1)
						actionTypeRef.current = animationActions.filter
						setPlatformFilters(newOptions)
					}}
				/>
				<MultiChoiceForm
					label="Difficulty:"
					options={getAvailableFilters(levelKey, levelFilters)}
					handler={(newOptions) => {
						setCurrentPage(1)
						setLevelFilters(newOptions)
					}}
					tooltipHeader="Estimate may vary, based on skill and experience."
					showTooltips
				/>
				<Pager
					currentPage={currentPage}
					allPage={allPage}
					getPage={(newPage) => {
						actionTypeRef.current = animationActions.page
						setCurrentPage(newPage)
					}}
				/>
			</div>
			<div
				className={classes.daresContainer}
				ref={wrapperRef}
			>
				{
					map(item => (
						<DareCardDrop
							className={classes.card}
							hover={item.prizeAvailable && !item.userCreated}
							dare={item}
							canAccept={canAccept}
							campaignId={campaignId}
							submitting={submitting}
							setSubmitting={setSubmitting}
							key={item.title + item.description}
							comingSoon={comingSoon}
							emailSubscription={emailSubscription}
							toggleEmailSubscription={toggleEmailSubscription}
							canAccessCreateBrandProject={canAccessCreateBrandProject}
							showTermsModal={showTermsModal}
							pointsToEarn={pointsToEarn}
							canEarnPoints={canEarnPoints}
							isAbleToParticipate={isAbleToParticipate}
						/>
					), pageItems)
				}
			</div>
		</div>
	)
}

export default withStyles(style, { name: 'MuiDaresSection' })(trackWindowScroll(DaresSection))
